import { Box, Button as MuiButton, InputBase, makeStyles } from '@material-ui/core';
import { InsertDriveFileOutlined, PublishOutlined } from '@material-ui/icons';
import { FileUpload } from '@roc/ui';
import { useDropzone } from 'react-dropzone';
import { RESOLUTION_COLORS } from './resolutionSummary';

// Same for every item: say what happened and where it stands now.
const EXPLANATION_PLACEHOLDER = 'Explain what happened and the current status of this item. If you have a document that supports it, upload it below.';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    color: RESOLUTION_COLORS.text,
    fontFamily: theme.typography.fontFamily,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    padding: '0 12px',
    fontSize: 14,
    lineHeight: '20px',
  },
  fieldTitle: {
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 700,
  },
  textarea: {
    alignItems: 'flex-start',
    padding: 16,
    minHeight: 82,
    borderRadius: 24,
    border: `1px solid ${RESOLUTION_COLORS.border}`,
    fontSize: 14,
    lineHeight: '20px',
    '&.Mui-focused': {
      borderColor: RESOLUTION_COLORS.brand,
    },
  },
  placeholder: {
    '& textarea::placeholder': {
      color: RESOLUTION_COLORS.muted,
      opacity: 1,
    },
  },
  dropzone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    padding: 24,
    textAlign: 'center',
    borderRadius: 16,
    border: `1px dashed ${RESOLUTION_COLORS.border}`,
    color: RESOLUTION_COLORS.muted,
    fontSize: 14,
    lineHeight: '20px',
    cursor: 'pointer',
    outline: 'none',
  },
  dropzoneActive: {
    backgroundColor: RESOLUTION_COLORS.infoBackground,
  },
  pill: {
    height: 40,
    padding: '8px 12px',
    borderRadius: 9999,
    border: `1px solid ${RESOLUTION_COLORS.border}`,
    backgroundColor: '#fff',
    color: RESOLUTION_COLORS.text,
    fontSize: 14,
    textTransform: 'none',
  },
  file: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '12px 16px',
    borderRadius: 16,
    border: `1px solid ${RESOLUTION_COLORS.border}`,
    fontSize: 14,
    lineHeight: '20px',
  },
  fileName: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    wordBreak: 'break-all',
  },
  remove: {
    textTransform: 'none',
    fontSize: 14,
  },
  submit: {
    height: 56,
    borderRadius: 9999,
    backgroundColor: RESOLUTION_COLORS.brand,
    color: '#ecf7ff',
    fontSize: 16,
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#0a2c44',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: RESOLUTION_COLORS.brand,
      color: '#ecf7ff',
      opacity: 0.5,
    },
  },
  helper: {
    textAlign: 'center',
    fontSize: 14,
    lineHeight: '20px',
    color: RESOLUTION_COLORS.muted,
  },
}));

export type ResolutionSubmitSectionProps = {
  comment: string;
  onCommentChange: (comment: string) => void;
  uploads: FileUpload[];
  setUploads: (uploads: FileUpload[]) => void;
  // Read-only: the explanation and the document can no longer be changed.
  disabled: boolean;
  canSubmit: boolean;
  onSubmit: () => void;
};

export const ResolutionSubmitSection = ({
  comment,
  onCommentChange,
  uploads,
  setUploads,
  disabled,
  canSubmit,
  onSubmit,
}: ResolutionSubmitSectionProps) => {
  const classes = useStyles();
  const upload = uploads[0];

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    multiple: false,
    maxFiles: 1,
    noClick: true,
    noKeyboard: true,
    onDrop: files => {
      const file = files[0];
      if (file) setUploads([{ name: file.name, file, type: file.name.split('.').pop() } as FileUpload]);
    },
  });

  return (
    <Box className={classes.root}>
      <Box className={classes.field}>
        <label className={classes.label} htmlFor="resolution-explanation">
          Your explanation
        </label>
        <InputBase
          id="resolution-explanation"
          multiline
          minRows={2}
          fullWidth
          disabled={disabled}
          value={comment}
          placeholder={EXPLANATION_PLACEHOLDER}
          onChange={event => onCommentChange(event.target.value)}
          className={classes.placeholder}
          classes={{ root: classes.textarea }}
          inputProps={{ 'data-testid': 'shareUrl' }}
        />
      </Box>

      {!disabled && (
        <Box className={classes.field}>
          <span className={classes.fieldTitle}>Supporting document</span>
          {upload ? (
            <Box className={classes.file}>
              <span className={classes.fileName}>
                <InsertDriveFileOutlined style={{ fontSize: 16 }} />
                {upload.name}
              </span>
              <MuiButton className={classes.remove} size="small" onClick={() => setUploads([])}>
                Remove
              </MuiButton>
            </Box>
          ) : (
            <Box {...getRootProps({ className: `${classes.dropzone} ${isDragActive ? classes.dropzoneActive : ''}` })}>
              <input {...getInputProps()} />
              <PublishOutlined style={{ fontSize: 24 }} />
              <span>Drag a file here</span>
              <MuiButton className={classes.pill} variant="outlined" onClick={open}>
                Choose file
              </MuiButton>
            </Box>
          )}
        </Box>
      )}

      <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
        <MuiButton
          fullWidth
          variant="contained"
          className={classes.submit}
          disabled={disabled || !canSubmit}
          onClick={onSubmit}
          data-testid="save"
        >
          Submit resolution
        </MuiButton>
        {!disabled && <span className={classes.helper}>Add an explanation, a document, or both.</span>}
      </Box>
    </Box>
  );
};
